import { Skeleton, SkeletonItem, Tooltip } from "@fluentui/react-components";
import { MetricInfo } from "../MetricInfo/MetricInfo";
import { useStyles } from "./StatCard.styles";
import type { IStatCardProps } from "./StatCard.types";

const UNAVAILABLE_FALLBACK_TEXT = "Not available for this selection";

export const StatCard = ({
  label,
  subtitle,
  value,
  delta,
  higherIsBetter = true,
  pending,
  warning,
  loading,
  info,
  unavailable,
  unavailableMessage,
}: IStatCardProps): JSX.Element => {
  const styles = useStyles();

  if (loading) {
    return (
      <div className={styles.card}>
        <div className={styles.label}>
          {label}
          {info && <MetricInfo info={info} label={label} />}
        </div>
        <div className={styles.subtitle}>{subtitle}</div>
        <Skeleton aria-label={`Loading ${label}`}>
          <SkeletonItem className={styles.skeletonValue} />
          <SkeletonItem className={styles.skeletonDelta} />
        </Skeleton>
      </div>
    );
  }

  if (unavailable) {
    const message = unavailableMessage ?? UNAVAILABLE_FALLBACK_TEXT;
    return (
      <div className={styles.card}>
        <div className={styles.label}>
          {label}
          {info && <MetricInfo info={info} label={label} />}
        </div>
        <div className={styles.subtitle}>{subtitle}</div>
        <Tooltip content={message} relationship="label">
          <div
            className={styles.unavailablePlaceholder}
            tabIndex={0}
            role="note"
          >
            {message}
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      {pending ? (
        <div className={styles.pendingBadge}>Data Pending</div>
      ) : warning ? (
        <div className={styles.warningBadge}>Requires Instrumentation</div>
      ) : null}
      <div className={styles.label}>
        {label}
        {info && <MetricInfo info={info} label={label} />}
      </div>
      <div className={styles.subtitle}>{subtitle}</div>
      <div className={pending ? styles.valuePending : styles.value}>
        {value}
      </div>
      {!pending && delta !== undefined && (
        <div
          className={`${styles.deltaBadge} ${(higherIsBetter ? delta >= 0 : delta <= 0) ? styles.deltaPositive : styles.deltaNegative}`}
        >
          {delta >= 0 ? "+" : ""}
          {delta}%
        </div>
      )}
    </div>
  );
};
